'use client';

// The shared presentational shell for ChatGPT-style table cards in the Master chat.
// It owns the exact ChatGPT table spec (measured from chatgpt.com) plus the minimal
// right-aligned copy / download toolbar, so the Analyst card and the Source card render
// identically and can never visually drift. Each card supplies its formatted grid
// ({header, rows, colAlign}) and any extra toolbar buttons (e.g. the Analyst pencil).
// Cells are text or text + a small logo/avatar (Source brand/contact rows).
import React, { useCallback, useMemo, useState } from 'react';
import { Copy, Download, Check, X } from '@/components/ui/lucide-memo';
import { type TableSlice, type SliceCell, cellText } from '@/components/tables/analystTableSlice';
import { buildCsvContent, downloadCsv } from '@/lib/portal/csvExport';

interface ChatTableCardShellProps {
  grid: TableSlice;
  title?: string;
  /** Extra toolbar buttons rendered after copy/download (e.g. the Analyst open-in-editor pencil). */
  extraActions?: React.ReactNode;
  /** Drop columns that are empty for every row (Source cards). Analyst spreadsheets keep
   *  all columns even when empty, so it defaults off. */
  dropEmptyCols?: boolean;
  /** Optional footer inside the card, below the table (e.g. a "See more in Source" row). */
  footer?: React.ReactNode;
}

const LONG_CELL_CHARS = 60;

// Static style objects live at module scope so the shell never hands a fresh object
// reference to the DOM (or to a memoized child) on a re-render.
const OUTER_STYLE: React.CSSProperties = { margin: '10px 0' };
const CARD_STYLE: React.CSSProperties = {
  borderRadius: 16,
  border: '1px solid #2E2E2E',
  background: '#1C1C1C',
  overflow: 'hidden',
};
// A fixed-height window into the shortlist: scroll vertically to see all rows
// while the card keeps its size; the header stays pinned. No vertical padding
// here (the header/cell padding gives the breathing room) so there is no empty
// band above or below the rows.
const SCROLLER_STYLE: React.CSSProperties = {
  overflowX: 'auto',
  overflowY: 'auto',
  maxHeight: 520,
  padding: '0 12px',
};
// Exact ChatGPT table spec (measured from chatgpt.com): border-collapse
// separate, 14px system font, th 600 + 1px header underline, td 400 with a
// faint row divider, numeric columns right-aligned.
const TABLE_STYLE: React.CSSProperties = {
  borderCollapse: 'separate',
  borderSpacing: 0,
  // Override the global `.chat-message-content-area table { margin: 1em 0 }`,
  // which otherwise adds an empty band above and below the rows in the card.
  margin: 0,
  // Size to content (not 100%): with short columns kept on one line and the
  // long columns capped, the table takes its natural width and the container
  // scrolls horizontally when it is wider than the card, instead of squeezing
  // every column to nothing.
  fontSize: 14,
  lineHeight: '20px',
  fontFamily: '-apple-system, ui-sans-serif, system-ui, "Segoe UI", Helvetica, Arial, sans-serif',
  fontVariantNumeric: 'tabular-nums',
};
const TH_STYLE: React.CSSProperties = {
  border: 'none',
  borderBottom: '1px solid #3E3E3E',
  // Card background (not the grey global th fill) so the pinned header
  // occludes rows scrolling under it without looking filled.
  background: '#1C1C1C',
  position: 'sticky',
  top: 0,
  zIndex: 1,
  textAlign: 'left',
  fontWeight: 600,
  color: '#FFFFFF',
  padding: '10px 12px 8px',
  whiteSpace: 'nowrap',
};
const TOOLBAR_STYLE: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-end',
  gap: 2,
  marginTop: 4,
};
const OVERLAY_STYLE: React.CSSProperties = {
  position: 'fixed',
  inset: 0,
  zIndex: 1000,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  background: 'rgba(0,0,0,0.6)',
  padding: 20,
};
const DIALOG_STYLE: React.CSSProperties = {
  width: 'min(560px, 92vw)',
  maxHeight: '70vh',
  overflowY: 'auto',
  background: '#1C1C1C',
  border: '1px solid #2E2E2E',
  borderRadius: 16,
  padding: '16px 18px',
};
const DIALOG_HEADER_STYLE: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  marginBottom: 8,
};
const DIALOG_TITLE_STYLE: React.CSSProperties = { fontSize: 13, fontWeight: 600, color: '#B5B5B5' };
const DIALOG_TEXT_STYLE: React.CSSProperties = {
  fontSize: 14,
  lineHeight: '22px',
  color: '#FFFFFF',
  whiteSpace: 'pre-wrap',
  wordBreak: 'break-word',
};
const LOGO_ROW_STYLE: React.CSSProperties = { display: 'inline-flex', alignItems: 'center', gap: 8 };
const LOGO_IMG_STYLE: React.CSSProperties = {
  flex: '0 0 auto',
  width: 18,
  height: 18,
  borderRadius: 4,
  objectFit: 'contain',
  background: '#FFFFFF',
};
const CLAMP_STYLE: React.CSSProperties = {
  display: '-webkit-box',
  WebkitLineClamp: 3,
  WebkitBoxOrient: 'vertical',
  overflow: 'hidden',
  whiteSpace: 'normal',
};

const hideBrokenLogo: React.ReactEventHandler<HTMLImageElement> = (e) => {
  (e.currentTarget as HTMLImageElement).style.display = 'none';
};
const stopPropagation = (e: React.MouseEvent) => e.stopPropagation();
const rowToTsv = (r: SliceCell[]): string => r.map(cellText).join('\t');

// Normalize a grid at RENDER time so any snapshot renders coherently, including older
// ones with no colWrap or with untrimmed columns: optionally drop all-empty columns, and
// decide per column whether to clamp (explicit colWrap when present, else derived from
// content length). This is what keeps a stale snapshot from rendering a huge nowrap wall.
function normalizeGrid(grid: TableSlice, dropEmptyCols: boolean): {
  header: string[];
  rows: SliceCell[][];
  colAlign: ('left' | 'right')[];
  colWrap: boolean[];
} {
  const n = grid.header.length;
  let idx = Array.from({ length: n }, (_, i) => i);
  if (dropEmptyCols) {
    const keep = idx.filter((ci) => grid.rows.some((r) => cellText(r[ci] ?? '') !== ''));
    if (keep.length > 0) idx = keep;
  }
  return {
    header: idx.map((ci) => grid.header[ci] ?? ''),
    rows: grid.rows.map((r) => idx.map((ci) => r[ci] ?? '')),
    colAlign: idx.map((ci) => grid.colAlign[ci] ?? 'left'),
    colWrap: idx.map(
      (ci) => grid.colWrap?.[ci] ?? grid.rows.some((r) => cellText(r[ci] ?? '').length > LONG_CELL_CHARS)
    ),
  };
}

const CellContent = React.memo(function CellContent({ cell, clamp }: { cell: SliceCell; clamp?: boolean }) {
  const text = typeof cell === 'string' ? cell : cell.text;
  const logoUrl = typeof cell === 'string' ? undefined : cell.logoUrl;
  if (logoUrl) {
    return (
      <span style={LOGO_ROW_STYLE}>
        <img
          src={logoUrl}
          alt=""
          width={18}
          height={18}
          loading="lazy"
          onError={hideBrokenLogo}
          style={LOGO_IMG_STYLE}
        />
        <span>{text}</span>
      </span>
    );
  }
  // Long-text columns clamp to 3 lines with an ellipsis so a huge rationale never
  // dominates the card; the full text is still in copy / download.
  if (clamp) {
    return <span style={CLAMP_STYLE}>{text}</span>;
  }
  return <>{text}</>;
});

// One memoized cell so the double-click closure is rebuilt only when that cell's own
// data changes, not on every shell re-render (chat streaming, copied/cellDetail state).
const TableCell = React.memo(function TableCell({
  cell,
  clamp,
  colTitle,
  style,
  onOpenDetail,
}: {
  cell: SliceCell;
  clamp: boolean;
  colTitle: string;
  /** Optional only for the type-checker: rows and columns are the same length by construction. */
  style?: React.CSSProperties;
  onOpenDetail: (title: string, text: string) => void;
}) {
  const handleDoubleClick = useCallback(
    () => onOpenDetail(colTitle, cellText(cell)),
    [onOpenDetail, colTitle, cell]
  );
  return (
    <td title="Double-click for full text" onDoubleClick={handleDoubleClick} style={style}>
      <CellContent cell={cell} clamp={clamp} />
    </td>
  );
});

export function ChatTableCardShell({ grid, title, extraActions, dropEmptyCols, footer }: ChatTableCardShellProps) {
  const [copied, setCopied] = useState(false);
  // Double-click a cell to read its full (un-clamped) text in a dialog.
  const [cellDetail, setCellDetail] = useState<{ title: string; text: string } | null>(null);
  const view = useMemo(() => normalizeGrid(grid, !!dropEmptyCols), [grid, dropEmptyCols]);

  // Per-column styles, computed once per grid: header cells, body cells, and the
  // last-row variant (no divider under the final row).
  const thStyles = useMemo(
    () => view.colAlign.map((textAlign) => ({ ...TH_STYLE, textAlign })),
    [view]
  );
  const tdStyles = useMemo(() => {
    const build = (isLastRow: boolean): React.CSSProperties[] =>
      view.colAlign.map((textAlign, ci) => {
        const wrap = view.colWrap[ci] ?? false;
        return {
          border: 'none',
          borderBottom: isLastRow ? 'none' : '1px solid #272727',
          color: '#FFFFFF',
          fontWeight: 400,
          padding: '10px 12px',
          verticalAlign: 'top',
          textAlign,
          whiteSpace: wrap ? 'normal' : 'nowrap',
          minWidth: wrap ? 240 : undefined,
          maxWidth: wrap ? 340 : undefined,
        };
      });
    return { rows: build(false), lastRow: build(true) };
  }, [view]);

  const openCellDetail = useCallback((detailTitle: string, text: string) => {
    setCellDetail({ title: detailTitle, text });
  }, []);
  const closeCellDetail = useCallback(() => setCellDetail(null), []);

  const copyTable = useCallback(() => {
    const tsv = [view.header.join('\t'), ...view.rows.map(rowToTsv)].join('\n');
    navigator.clipboard
      ?.writeText(tsv)
      .then(() => {
        setCopied(true);
        setTimeout(() => setCopied(false), 1500);
      })
      .catch(() => {});
  }, [view]);
  const exportCsv = useCallback(() => {
    const csv = buildCsvContent(
      view.header,
      view.rows.map((r) => r.map(cellText))
    );
    downloadCsv((title || grid.sheetName || 'table').replace(/[^\w .-]/g, '').trim() || 'table', csv);
  }, [view, title, grid.sheetName]);

  return (
    <>
    <div style={OUTER_STYLE}>
    <div style={CARD_STYLE}>
      <div style={SCROLLER_STYLE}>
        <table style={TABLE_STYLE}>
          <thead>
            <tr>
              {view.header.map((h, i) => (
                <th key={i} style={thStyles[i]}>
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {view.rows.map((row, ri) => {
              const isLastRow = ri === view.rows.length - 1;
              const styles = isLastRow ? tdStyles.lastRow : tdStyles.rows;
              return (
              <tr key={ri}>
                {row.map((cell, ci) => (
                  <TableCell
                    key={ci}
                    cell={cell}
                    clamp={view.colWrap[ci] ?? false}
                    colTitle={view.header[ci] ?? ''}
                    style={styles[ci]}
                    onOpenDetail={openCellDetail}
                  />
                ))}
              </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {footer}
    </div>
    {/* Copy / download live OUTSIDE the card (below it), right-aligned, with no divider
        stroke, so the card box is just the table. */}
    <div style={TOOLBAR_STYLE}>
      <button
        type="button"
        onClick={copyTable}
        title="Copy"
        className="inline-flex h-8 w-8 items-center justify-center rounded-lg text-[#B5B5B5] transition-colors hover:bg-[#2A2A2A] hover:text-white"
      >
        {copied ? <Check size={16} strokeWidth={2.4} /> : <Copy size={16} strokeWidth={2} />}
      </button>
      <button
        type="button"
        onClick={exportCsv}
        title="Download CSV"
        className="inline-flex h-8 w-8 items-center justify-center rounded-lg text-[#B5B5B5] transition-colors hover:bg-[#2A2A2A] hover:text-white"
      >
        <Download size={16} strokeWidth={2} />
      </button>
      {extraActions}
    </div>
    </div>
    {cellDetail && (
      <div onClick={closeCellDetail} style={OVERLAY_STYLE}>
        <div onClick={stopPropagation} style={DIALOG_STYLE}>
          <div style={DIALOG_HEADER_STYLE}>
            <div style={DIALOG_TITLE_STYLE}>{cellDetail.title || 'Detail'}</div>
            <button
              type="button"
              onClick={closeCellDetail}
              className="inline-flex h-7 w-7 items-center justify-center rounded-lg text-[#B5B5B5] transition-colors hover:bg-[#2A2A2A] hover:text-white"
            >
              <X size={16} strokeWidth={2} />
            </button>
          </div>
          <div style={DIALOG_TEXT_STYLE}>
            {cellDetail.text || '(empty)'}
          </div>
        </div>
      </div>
    )}
    </>
  );
}

// A pulsing table placeholder shown while a table-card marker is still streaming, so
// the user never sees the raw marker JSON. Same card chrome as the real table.
export function ChatTableSkeleton({ rows = 6, cols = 6 }: { rows?: number; cols?: number }) {
  const mid = Math.floor(cols / 2);
  const bar = (bg: string, flex: number) => (
    <div style={{ height: 9, borderRadius: 4, background: bg, flex }} />
  );
  return (
    <div style={{ margin: '10px 0', borderRadius: 16, border: '1px solid #2E2E2E', background: '#1C1C1C', overflow: 'hidden' }}>
      <div className="animate-pulse" style={{ padding: '16px 16px 14px' }}>
        <div style={{ display: 'flex', gap: 16, marginBottom: 14 }}>
          {Array.from({ length: cols }).map((_, i) => (
            <React.Fragment key={i}>{bar('#3A3A3A', i === mid ? 3 : 1)}</React.Fragment>
          ))}
        </div>
        {Array.from({ length: rows }).map((_, r) => (
          <div key={r} style={{ display: 'flex', gap: 16, padding: '11px 0', borderTop: '1px solid #232323' }}>
            {Array.from({ length: cols }).map((_, i) => (
              <React.Fragment key={i}>{bar('#2A2A2A', i === mid ? 3 : 1)}</React.Fragment>
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}
